import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFAQ } from "../index";
import { FAQAccordion } from "../ui/FAQAccordion";

interface SupportFAQProps {
  pageSize?: number;
}

/**
 * SupportFAQ - FAQ 탭 콘텐츠 컴포넌트
 */
export const SupportFAQ = ({ pageSize = 10 }: SupportFAQProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // FAQ API 연동
  const { faqList, total, isLoading, error } = useFAQ({
    page: currentPage,
    pageSize: pageSize,
    category: "faq",
  });

  return (
    <div className="w-full flex flex-col gap-12 md:gap-14">
      {/* FAQ Header */}
      <div className="flex flex-col gap-6">
        <p className="text-lg text-gray-400">고객지원</p>
        <h1 className="text-3xl md:text-4xl font-normal text-black leading-tight">
          자주 묻는 질문
        </h1>
        <p className="text-base md:text-lg text-black leading-7">
          좋은성장 이용에 대해 자주 묻는 질문을 모았습니다. 여기서 답변을 찾기
          어렵다면, 언제든지 문의해 주세요.
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <p className="text-gray-500">FAQ를 불러오는 중...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex justify-center items-center py-12">
          <p className="text-red-500">FAQ를 불러올 수 없습니다. {error}</p>
        </div>
      )}

      {/* FAQ Accordion with API Data */}
      {!isLoading && !error && faqList.length > 0 && (
        <>
          <div className="w-full">
            <FAQAccordion items={faqList} />
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex justify-center gap-2">
              {Array.from(
                { length: Math.ceil(total / pageSize) },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 flex items-center justify-center rounded ${
                    currentPage === page
                      ? "border border-gray-300 text-black font-semibold bg-gray-100"
                      : "border border-gray-300 text-gray-400 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
              {Math.ceil(total / pageSize) > 1 && (
                <button className="px-3 h-10 flex items-center justify-center rounded border border-gray-300 text-gray-400 hover:bg-gray-50">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Empty State (when no data from API) */}
      {!isLoading && !error && faqList.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <p className="text-gray-500">등록된 FAQ가 없습니다.</p>
        </div>
      )}

      {/* Contact Button */}
      <div className="flex justify-end pt-8 border-t border-gray-200">
        <button
          onClick={() => {
            window.location.href = "/support?tab=inquiry";
          }}
          className="px-8 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium text-base transition-colors"
        >
          고객문의하기
        </button>
      </div>
    </div>
  );
};
